import { useState, useEffect, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { type UserWithRole } from '~entities/users/model/types';
import { useGetUserRoles } from '~entities/users/api/get-user-roles';
import { useUpdateUserRole } from '~entities/users/api/update-user-role';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Button } from '~shared/shadcn/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { Label } from '~shared/shadcn/ui/label';

interface EditUserRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserWithRole | null;
  onClose: () => void;
}

export const EditUserRoleDialog = ({
  open,
  onOpenChange,
  user,
  onClose,
}: EditUserRoleDialogProps) => {
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const { data: userRoles, isLoading: isLoadingRoles } = useGetUserRoles();
  const updateUserRoleMutation = useUpdateUserRole();

  // Filter out the user's current role - only show roles that are different from the current one
  const availableRoles = useMemo(() => {
    if (!userRoles || !user?.role.name) {
      return userRoles ?? [];
    }
    const currentRole = user.role.name;
    return userRoles.filter(
        (role) => 
            role.name && 
            role.name.toLowerCase() !== currentRole.toLowerCase()
        );
  }, [userRoles, user?.role.name]);

  // Reset selected role when dialog opens/closes or user changes
  useEffect(() => {
    if (!open) {
      setSelectedRoleId('');
    }
  }, [open, user]);

  const handleClose = () => {
    setSelectedRoleId('');
    onClose();
  };

  const handleSubmit = () => {
    if (!user?.id || !selectedRoleId) {
      toast.error('Please select a role');
      return;
    }

    const selectedRole = userRoles?.find((role) => role.id === selectedRoleId);
    const userName = `${user.firstName} ${user.lastName}`;
    const roleName = selectedRole?.name ?? 'selected role';

    updateUserRoleMutation.mutate(
      {
        userId: user.id,
        roleId: selectedRoleId,
      },
      {
        onSuccess: () => {
          toast.success(
            `User role updated successfully. ${userName} now has the role: ${roleName}`,
          );
          handleClose();
        },
        onError: () => {
          toast.error(`Failed to update role for ${userName}. Please try again.`);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit User Role</DialogTitle>
          <DialogDescription>
            Select a new role for {user?.firstName} {user?.lastName}
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-4 py-4'>
          <div className='grid gap-2'>
            <Label htmlFor='role-select'>New Role</Label>
            {isLoadingRoles ? (
              <div className='flex items-center justify-center py-4'>
                <Loader2 className='size-4 animate-spin' />
              </div>
            ) : (
              <Select
                value={selectedRoleId}
                onValueChange={setSelectedRoleId}
              >
                <SelectTrigger id='role-select'>
                  <SelectValue placeholder='Select a role' />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role.id} value={role.id ?? ''}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedRoleId || updateUserRoleMutation.isPending}
          >
            {updateUserRoleMutation.isPending ? (
              <>
                <Loader2 className='mr-2 size-4 animate-spin' />
                Updating...
              </>
            ) : (
              'Submit'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

